import React, { useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import Toast from 'react-native-toast-message';
import DeleteModal from '../../../components/DeleteModal';
import { dispatchService } from '../../../services/central-api/dispatch';
import { Pagination } from '../../../components/Pagination';
import { ClientError } from '../../../services/central-api/base-service';
import MyDataTable from '../../../components/MyDataTable';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { DispatchParamList } from '../../../typings/Params';
import useFilters from '../../../components/Filter/hooks/useFilters';
import FilterInput from '../../../components/Filter/components/FilterInput';
import CustomButton from '../../../components/CustomButton';
import Filter from './Filters';
import { useAuth } from '../../../context/Auth';
import DropdownButton from '../../../components/DropdownButton';
import { useQuery } from '@tanstack/react-query';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useStyles } from 'react-native-unistyles';
import Tooltip from '../../../components/Tooltip';
import PulsingText from '../../../components/PulsingText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWifi } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';

export default function TeamNames({ navigation }: { navigation: Navigation; }) {
    const { theme } = useStyles();

    const route = useRoute<RouteProp<DispatchParamList, 'TeamNames'>>();
    const { user: authenticatedUser } = useAuth();

    // filters
    const [filters, setFilters] = useFilters<DispatchParamList['TeamNames']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });

    const [selectedTeamName, setSelectedTeamName] = useState<TeamName>();
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['getTeamNames', filters],
        queryFn: () => dispatchService.getTeamNames(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    function handleEditClick(teamName: TeamName) {
        setSelectedTeamName(teamName);
        navigation.navigate('TeamName', { id: teamName.id });
    }

    function showDeleteConfirmationDialog(teamName: TeamName) {
        setSelectedTeamName(teamName);
        setDeleteModalVisible(true);
    }

    async function confirmDelete(teamName: TeamName) {
        try {
            await dispatchService.deleteTeamName(teamName.id);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            setDeleteModalVisible(false);
            refetch();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    return <Container>
        <Header
            title={translate('teamNames')}
            actions={
                <CustomButton text={translate('register')} onPress={() => {
                    setSelectedTeamName(undefined);
                    navigation.navigate('TeamName');
                }} />
            }
        />
        <Card>
            <DataTableHeader
                zIndex={2}
                title={translate('registeredTeamNames')}
                filter={<Filter
                    filters={filters}
                    setFilters={setFilters}
                    textFilter={textFilter}
                    setTextFilter={setTextFilter} />}
                filterInput={<FilterInput
                    value={textFilter}
                    placeholder={translate('typeToSearch')}
                    onChange={setTextFilter}
                    onKeyPress={handleKeyDown}
                />}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[{
                        name: translate('Id'),
                        selector: row => row.id,
                        grow: 0
                    },
                    {
                        name: translate('name'),
                        selector: row => row.name,
                        wrap: true,
                        grow: 3
                    },
                    {
                        name: translate('eventReceiverIdentifier'),
                        selector: row => row.eventReceiverIdentifier ? row.eventReceiverIdentifier : '',
                        wrap: true,
                    },
                    {
                        name: translate('category'),
                        selector: row => row.category ? translate(row.category) : '',
                        wrap: true,
                    },
                    {
                        name: translate('actingBody'),
                        selector: row => row.actingBody.name ?? '',
                        wrap: true,
                    },
                    {
                        name: translate('unit'),
                        selector: row => row.actingBodyUnit ? row.actingBodyUnit.name : '',
                        wrap: true,
                    },
                    {
                        name: translate('command'),
                        selector: row => row.actingBodyCommand ? row.actingBodyCommand.name : '',
                        wrap: true,
                    },
                    {
                        name: translate('liveStream'),
                        cell: row => <Tooltip
                            tooltipDirection='right'
                            tooltipContent={translate(row.isConnected ? 'femaleConnected' : 'femaleDisconnected')}
                        >
                            <PulsingText
                                myKey={row.id}
                                fromColor={row.isConnected ? theme.colors.lightGreen : theme.colors.neutral[300]}
                                toColor={row.isConnected ? theme.colors.green : theme.colors.neutral[300]}
                            >
                                <FontAwesomeIcon icon={faWifi as IconDefinition} size='lg' />
                            </PulsingText>
                        </Tooltip>,
                        maxWidth: '160px',
                        minWidth: '160px',
                        center: true,
                    },
                    {
                        button: true,
                        cell: row =>
                            <DropdownButton
                                actions={[
                                    {
                                        content: translate('editInformation'),
                                        onPress: () => handleEditClick(row)
                                    },
                                    {
                                        content: translate('delete'),
                                        variant: 'danger',
                                        onPress: () => showDeleteConfirmationDialog(row)
                                    }
                                ]}
                            />
                    }]}
                    data={data?.rows ?? []}
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    currentPage={filters.page}
                    totalItems={data?.count ?? 0}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                />}
            />
        </Card>

        {selectedTeamName &&
            <DeleteModal
                setModalVisible={setDeleteModalVisible}
                isModalVisible={isDeleteModalVisible}
                itemName={selectedTeamName.name}
                onSubmit={async () => {
                    await confirmDelete(selectedTeamName);
                }}
            />
        }

    </Container >;
}
